import React, { useRef, useState } from 'react';
import { Button, Icon } from '@fasstech/middle-design-system';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { useMutation, useQuery } from '@apollo/client';
import { MCreateJiraBug } from '../graphql/mutations/MCreateJiraBug';
import CreateBugModal from './CreateBugModal';
import { applySpec, find, pathOr, propOr } from 'ramda';
import withMiddleFeatures from '@@viewCommonHoc/withMiddleFeatures.jsx';
import { QGlobalDataQuery } from '@@app/graphql/index.js';
import { useBreadcrumb } from '@@appComponents/Header.jsx';

const QAButton = ({
  middleFeatures,
  getMiddleText
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreatingBug, setIsCreatingBug] = useState(false);
  const [bugInfo, setBugInfo] = useState(null);
  const dropdownRef = useRef(null);
  const { context } = useProcess();
  const [createJiraBug] = useMutation(MCreateJiraBug);
  const { views } = useBreadcrumb(middleFeatures);

  const {
    data
  } = useQuery(QGlobalDataQuery, {
    fetchPolicy: 'cache-and-network'
  });
  const projectNumber = pathOr('', ['getProject', 'data', 'projectNumber'], data).toString();

  const qaToolsEnabled = pathOr(false, ['getQAToolsEnabled', 'enabled'], data);

  if (!qaToolsEnabled) {
    return null;
  }

  const clientName = getMiddleText('GENERAL_CLIENT_NOM') || 'Middle';

  const openDropdown = () => {
    setIsDropdownOpen(true);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const handleCreateBug = () => {

    const {
      currentOfferCode,
      currentViewId
    } = applySpec({
      currentOfferCode: propOr('', 'offerCode'),
      currentViewId: propOr('', 'viewId')
    })(context);

    const offerCodeLabels = {
      INDIVIDUAL_HEALTH: 'Santé Individuelle',
      GROUP_HEALTH: 'Santé Collective',
      INDIVIDUAL_PROTECTION_DAILY_BENEFIT: 'IJ Hospi',
      SELF_EMPLOYED_WORKER_PROTECTION: 'Prévoyance TNS',
      GROUP_PROTECTION: 'Prévoyance collective'
    };
    const risque = offerCodeLabels[currentOfferCode] || currentOfferCode;

    const currentView = find((view) => view.viewId === currentViewId, views);
    const viewLabel = propOr(currentViewId, 'label', currentView);

    setBugInfo({
      projectNumber,
      risque,
      viewLabel,
      viewId: currentViewId,
      url: window.location.href,
      environment: process.env.NODE_ENV || 'development',
      clientName
    });
    setIsModalOpen(true);
    closeDropdown();
  };

  const handleConfirmCreateBug = async ({
    title,
    description,
    board,
    priority,
    environment
  }) => {
    setIsCreatingBug(true);

    try {
      const { data: mutationData } = await createJiraBug({
        variables: {
          ...bugInfo,
          title,
          description,
          board: board.value,
          boardUrl: board.url,
          priority,
          jiraEnvironment: environment,
          projectData: data?.getProject
        }
      });

      if (mutationData?.createJiraBug?.ok) {

        const issueUrl = mutationData.createJiraBug.issueUrl;
        const issueKey = mutationData.createJiraBug.issueKey;
        alert(`✅ Ticket Jira créé avec succès : ${issueKey}`);
        window.open(issueUrl, '_blank');
        setIsModalOpen(false);
      } else {

        const error = mutationData?.createJiraBug?.error;
        alert(`❌ Erreur lors de la création du ticket Jira:\n${error}`);
      }
    } catch (error) {
      console.error('Failed to create Jira bug:', error);
      alert(`❌ Erreur lors de la création du ticket Jira:\n${error.message}`);
    } finally {
      setIsCreatingBug(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setBugInfo(null);
  };

  const handleDownloadProject = () => {
    try {
      const now = new Date();
      const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
      const filename = `project_${date}_${time}.json`;

      // Création du Blob et téléchargement
      const jsonString = JSON.stringify(data?.getProject, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      closeDropdown();
    } catch (error) {
      console.error('Failed to download project:', error);
      alert('Erreur lors du téléchargement du projet.');
    }
  };

  return (
    <>
      <div
        ref={dropdownRef}
        className="fixed bottom-6 left-6 z-50"
        onMouseEnter={openDropdown}
        onMouseLeave={closeDropdown}
      >
        <Button
          variant="primary"
          className="flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow duration-200">
          QA Tool
        </Button>

        <div
          className={`absolute bottom-full left-0 pb-2 transition-all duration-200 origin-bottom ${isDropdownOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-2 pointer-events-none'
          }`}
        >
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[210px]">
            <button
              onClick={handleCreateBug}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center gap-2">
              <Icon name="warning" />
              Créer un bug
            </button>
            <button
              onClick={handleDownloadProject}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center gap-2">
              <Icon name="download" />
              Télécharger le projet
            </button>
          </div>
        </div>
      </div>

      <CreateBugModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        bugInfo={bugInfo}
        onConfirm={handleConfirmCreateBug}
        isCreating={isCreatingBug}
        clientName={clientName}
      />
    </>
  );
};

export default withMiddleFeatures(QAButton);
